/* @flow */


import bidbase from './bidbase';
import { apiName, apiVersion, getRandom } from '../utils';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import {
  isJSONSupported,
  getViewportDimensions,
  BrowserUtil,
  getHostname,
  getLanguage,
  getPageUrl,
  getLocationHref
} from '../utils/browserutil';
import {
  buildUrl,
  extend,
  isArray,
  isObject,
  isEmptyObject
} from '../utils/coreutils';
import { logError } from '../utils/logger';
import { getRTBUserEids } from '../idproviders/idputils';
import {
  PREBID_UID_CRITEO,
  PREBID_UID_LIVERAMP,
  PREBID_UID_PUBCOMMON,
  PREBID_UID_UNIFIEDID
} from '../idproviders/idpconstants';
import globalParams from '../globalparams';
import { isDNT } from '../utils/privacyutils';

/**
 * typedef definitions
 */

declare type tripleliftConfig = hbPartnerConfig & {
  // flowlint-next-line unclear-type:off
  tagid: Object | string
};

declare type tripleliftSlotData = {
  id: string,
  tagid: string,
  banner: {
    format: Array<{ w: number, h: number }>
  }
};

declare type tripleliftReqParam = {
  id: string,
  imp: Array<tripleliftSlotData>
};

declare type tripleliftPosResponse = {
  ad: string,
  cpm: Number,
  imp_id: string,
  crid: string,
  width: Number,
  height: Number
};

declare type tripleliftResponse = { bids: Array<tripleliftPosResponse> };

declare type TripleLiftuid = {
  id: string | Array<string>,
  ext: {
    rtiPartner: string
  }
};

declare type TripleLiftUserID = {
  source: string,
  uids: Array<TripleLiftuid>
};

declare type TripleLiftEids = Array<TripleLiftUserID>;

declare type TripleLiftUserIDs = {|
  user: {
    ext: {
      eids: TripleLiftEids
    }
  }
|};

declare type deviceInfo = {|
  device: {
    w: number,
    h: number,
    dnt: number,
    ua: string,
    language: string,
    ext: {
      vpw: number,
      vph: number
    }
  }
|};

declare type regsInfo = {|
  regs: {
    ext: {
      us_privacy: string
    }
  }
|};

declare type contentInfo = {
  language: string
};

declare type siteExtInfo = {
  data: {
    documentLang: string
  }
};

declare type publisherInfo = {
  domain: string
};

declare type siteInfo = {|
  site: {
    domain: string,
    page: string,
    content: contentInfo,
    ext: siteExtInfo,
    publisher: publisherInfo
  }
|};

declare type sourceInfo = {|
  source?: {
    ext?: {
      schain?: anyObject
    }
  }
|};

declare type ortb2Info = {
  ...TripleLiftUserIDs,
  ...deviceInfo,
  ...regsInfo,
  ...siteInfo,
  ...sourceInfo
};

declare type contextInfo = {|
  context: {
    content: contentInfo,
    domain: string,
    ext: siteExtInfo,
    page: string,
    publisher: publisherInfo
  }
|};

declare type fpdInfo = {
  ...TripleLiftUserIDs,
  ...contextInfo
};

declare type extInfo = {
  ortb2: ortb2Info,
  fpd: fpdInfo
};

declare type TripleLiftBidRequestCache = {
  impid: {
    [string]: string
  }
};

const DEFAULT_END_POINT = 'https://tlx.3lif.com/header/auction';
const DEFAULT_SYNC_ENDPOINT = 'https://eb2.3lif.com/sync';

class _triplelift extends bidbase {
  _config: tripleliftConfig;

  _DefConfig: tripleliftConfig;

  _bidRequest: TripleLiftBidRequestCache;

  constructor(name: string = 'triplelift') {
    super(name);
    this._DefConfig = extend(true, {}, this._DefConfig, {
      bidUrl: DEFAULT_END_POINT,
      syncUrl: DEFAULT_SYNC_ENDPOINT
    });
    this._isSupported = isJSONSupported() && isFetchSupported();
    this._config = extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['bidUrl'];
    this._bidRequest = {
      impid: {}
    };
  }

  /**
   * Return tagid for position [ Tag ID can be brand specific, check and return accordingly ]
   *
   * @param {string} pos - Ad position
   * @param {string} brand - UOL Brand
   * @returns {string} - Returns tagid for the given position
   * @access private
   */
  _getTagId(pos: string, brand: string): string {
    const { tagid } = this._config.posTokenMap[pos];
    if (isObject(tagid)) {
      return tagid[brand];
    }
    return tagid;
  }

  /**
   * Create slot object for an ad position
   *
   * @param {string} pos - Ad position
   * @param {string} brand - UOL Brand
   * @returns {object} - Request URL info for given position
   * @access private
   */
  _getSlot(pos: string, brand: string): tripleliftSlotData {
    // get all the required params
    const randid = getRandom();
    const sizes = [...this._config.posTokenMap[pos].sizes];
    const format = sizes.map(([w, h]) => ({ w, h }));
    const params = {
      id: randid,
      tagid: this._getTagId(pos, brand),
      banner: {
        format
      }
    };
    this._bidRequest.impid[randid] = pos;
    return params;
  }

  /**
   * Build UserIDs for TripleLift
   *
   * @param {RTBUserIDs} userIDs - RTB User IDs
   * @returns {TripleLiftUserIDs} - User IDs transformed for TripleLift
   * @access private
   */
  _getUserIdsForTripleLift({
    user: { eids = [] } = {}
  }: {
    user: { eids: RTBEids }
  }): TripleLiftUserIDs {
    /* rtiPartner value map from Prebid */
    const srcRTIPartnerMap = {
      'adserver.org': 'TDID',
      'liveramp.com': 'idl',
      'criteo.com': 'criteoId',
      'pubcid.org': 'pubcid'
    };

    return {
      user: {
        ext: {
          eids: eids.map(eid => ({
            source: eid.source,
            uids: eid.uids.map(e => ({
              id: e.id,
              ext: { rtiPartner: srcRTIPartnerMap[eid.source] }
            }))
          }))
        }
      }
    };
  }

  _getOrtb2(userIds: RTBUserIDs): ortb2Info {
    const [vpw, vph] = getViewportDimensions();
    const schain = this.getSupplyChain();
    const { user: { ext: { eids = [] } = {} } = {} } =
      this._getUserIdsForTripleLift(userIds);
    return {
      device: {
        w: window.screen.width,
        h: window.screen.height,
        dnt: isDNT() ? 1 : 0,
        ua: BrowserUtil.getUserAgent(),
        language: getLanguage(),
        ext: {
          vpw,
          vph
        }
      },
      regs: {
        ext: {
          ...this._getPrivacyString()
        }
      },
      site: {
        domain: getHostname(),
        ext: {
          data: { documentLang: getLanguage() }
        },
        page: getPageUrl(),
        publisher: {
          domain: getHostname()
        },
        content: {
          language: getLanguage()
        }
      },
      ...(schain &&
        !isEmptyObject(schain) && {
          source: {
            ext: {
              schain
            }
          }
        }),
      user: {
        ext: {
          eids
        }
      }
    };
  }

  _getfpd(userIds: RTBUserIDs): fpdInfo {
    const { user: { ext: { eids = [] } = {} } = {} } =
      this._getUserIdsForTripleLift(userIds);
    return {
      context: {
        content: {
          language: getLanguage()
        },
        domain: getHostname(),
        ext: {
          data: { documentLang: getLanguage() }
        },
        page: getPageUrl(),
        publisher: {
          domain: getHostname()
        }
      },
      user: {
        ext: {
          eids
        }
      }
    };
  }

  _getExtInfo(userIds: RTBUserIDs): extInfo {
    const fpd = this._getfpd(userIds);
    const ortb2 = this._getOrtb2(userIds);
    const schain = this.getSupplyChain();
    return {
      fpd,
      ortb2,
      ...(schain && !isEmptyObject(schain) && { schain })
    };
  }

  /**
   * Builds and sends bid requests
   *
   * @param {Array<string>} posList - Ad positions
   * @returns {Promise} - Returns promise
   * @access private
   */
  _getBids(posList: Array<string>): Promise<tripleliftResponse> {
    const gparams = globalParams.get();
    const { brand } = gparams.phInfo;
    const slots = posList.map(pos => this._getSlot(pos, brand));
    const opt = {
      data: {},
      timeout: this._config.timeout,
      logPrefix: `${this.getName()} - ${posList.join(',')}`
    };

    const userIds = getRTBUserEids([
      PREBID_UID_CRITEO,
      PREBID_UID_LIVERAMP,
      PREBID_UID_PUBCOMMON,
      PREBID_UID_UNIFIEDID
    ]);
    const ext = this._getExtInfo(userIds);

    const params = extend(
      true,
      {},
      { ext },
      { imp: slots },
      this._getUserIdsForTripleLift(userIds)
    );

    opt.data = extend(
      true,
      opt.data,
      { lib: apiName() },
      { v: apiVersion() },
      this._getPrivacyString(),
      {
        referrer: getLocationHref()
      }
    );

    const req = {
      method: 'POST',
      body: JSON.stringify(params),
      credentials: 'include'
    };

    return fetchJSON(this._config.bidUrl, opt, req).then(
      (json: tripleliftResponse) => json
    );
  }

  /**
   * This method will be called for bidders witout overriden userSync method
   *
   * @external syncObj
   * @returns {Array<syncObj>} - Array of sync objects
   * @access private
   */
  _getUserSync(): Array<syncObj> {
    const ret = [];
    // make sure that syncUrl is not empty
    if (this._config.syncUrl.length > 0) {
      ret.push({
        name: this.getName(),
        type: 'image',
        url: buildUrl(
          this._config.syncUrl,
          extend(true, { px: 1, src: apiName() }, this._getPrivacyString())
        )
      });
    }
    return ret;
  }

  /**
   * Process bid response. Response format:
   *
   * @param {Array<string>} posList - HB position list
   * @param {object} response - Response from server
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {object.<string, hbPosData>} - Return data
   */
  _processBidResponse(
    posList: Array<string>,
    response: tripleliftResponse,
    ret: hbPartnerData
  ): hbPartnerData {
    const { bids } = response;
    if (!isArray(bids) || !bids.length) {
      return ret;
    }

    bids.forEach(bid => {
      // eslint-disable-next-line camelcase
      const { imp_id = '', cpm = 0, ad = '' } = bid;
      const price = String(cpm);
      // eslint-disable-next-line camelcase
      if (imp_id.length > 0 && price.length > 0 && ad.length > 0) {
        this._handleBidPosData(
          ret,
          // eslint-disable-next-line camelcase
          this._bidRequest.impid[imp_id],
          price,
          imp_id,
          ad
        );
      }
    });
    return ret;
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB Position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    return this._getBids(posList).then(
      response => this._processBidResponse(posList, response, ret),
      error => {
        logError(`${this._name} Failed to get bids: ${error || ''}`);
        return ret;
      }
    );
  }
}

// add bidder to bidmanager
const triplelift: _triplelift = new _triplelift();
export default triplelift;
